import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Button, Label, Input, Table } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../../layout/Header/Header";
import { epcToEAN13, validateEAN13 } from "../../../../utils/ean13Generator";

// Entrada de PreModelos: se escanean etiquetas RFID de PreModelos y al guardar,
// en un solo paso, se convierten a Modelo y se les da entrada (Compra).
//
// En bodega la pistola lee TODO (lo ya recibido y lo nuevo). Solo se aceptan
// PreModelos que todavía no son Modelo en Forever; lo demás se omite. La decisión
// la toma el backend (POST /entradasPreModelos/validar).
//
// El escaneo es el mismo de Nueva Entrada (ComprasCreate): el RFID es uno por
// modelo+color+talla, así que cada lectura de 13 dígitos suma una pieza. Solo si
// el lector entrega el EPC completo (24 hex, trae número de serie) se ignora el
// mismo tag leído dos veces.

const fmt = (n) => new Intl.NumberFormat("en-US").format(n);
const fmtMoney = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "MXN" }).format(n || 0);

function EntradasPreModelos() {
  const { user } = useContext(AuthContext);
  const URL_LOGS = import.meta.env.VITE_URL_LOGS;
  const URL_AREAS = import.meta.env.VITE_URL_AREAS;
  const URL_PROVEEDORES = import.meta.env.VITE_URL_PROVEEDORES;
  const URL_COMPRAS = import.meta.env.VITE_URL_COMPRAS;
  const AREAS_GENERAL = import.meta.env.VITE_AREAS_GENERAL;
  const URL_ENTRADAS_PRE_MODELOS =
    import.meta.env.VITE_URL_ENTRADAS_PRE_MODELOS ||
    (URL_COMPRAS || "").replace(/compras\/?$/i, "entradasPreModelos");

  const headers = () => ({
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  });

  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState("");
  const [proveedores, setProveedores] = useState([]);
  const [selectedProveedor, setSelectedProveedor] = useState("");

  // proporcionId -> { proporcion, preId, codigo, nombre, colorNombre, tallaNombre, cantidad }
  const [renglones, setRenglones] = useState({});
  // preArticuloId -> costo por pieza (prellenado con costoMX, editable)
  const [costos, setCostos] = useState({});
  const [stats, setStats] = useState({ leidas: 0, repetidas: 0, omitidas: 0 });
  // Lecturas en consulta al servidor: no se puede guardar mientras haya.
  const [pendientes, setPendientes] = useState(0);
  const [validaBoton, setValidaBoton] = useState(true);
  const submittingRef = useRef(false);

  // Campo de escaneo NO controlado (ver ComprasCreate / memoria RFID).
  const rfidScanRef = useRef(null);
  // EPC -> proporcionId | "omitido" | "" (en proceso). Solo aplica con EPC.
  const epcLeidosRef = useRef(new Map());
  // codigo -> promesa de la respuesta del backend, para no repetir la consulta
  // por cada pieza del mismo modelo/color/talla.
  const cacheRef = useRef(new Map());

  const DRAFT_KEY = `entrada_premodelos_${user?._id}`;
  const canSaveRef = useRef(false);

  useEffect(() => {
    if (user && user.areas !== undefined && user.areas !== AREAS_GENERAL) {
      setSelectedArea(user.areas);
    }
  }, [user]);

  useEffect(() => {
    axios
      .get(URL_AREAS, { headers: headers() })
      .then((res) => setAreas(res.data))
      .catch((err) => console.log(err));
    axios
      .get(URL_PROVEEDORES, { headers: headers() })
      .then((res) => setProveedores(res.data))
      .catch((err) => console.log(err));
  }, []);

  // ─── Borrador ───────────────────────────────────────────────────────────
  useEffect(() => {
    let draft = null;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (saved) draft = JSON.parse(saved);
    } catch (e) {
      console.log("Error al leer borrador", e);
    }
    if (!draft || !draft.renglones || !Object.keys(draft.renglones).length) {
      canSaveRef.current = true;
      return;
    }
    Swal.fire({
      title: "Entrada sin terminar",
      text: "Tienes una entrada de PreModelos en progreso. ¿Deseas continuar con ella o empezar una nueva?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Continuar con la anterior",
      cancelButtonText: "Empezar una nueva",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      allowOutsideClick: false,
      allowEscapeKey: false,
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        if (draft.fecha) setFecha(draft.fecha);
        if (draft.selectedArea) setSelectedArea(draft.selectedArea);
        if (draft.selectedProveedor) setSelectedProveedor(draft.selectedProveedor);
        setRenglones(draft.renglones || {});
        setCostos(draft.costos || {});
        if (Array.isArray(draft.epcs)) epcLeidosRef.current = new Map(draft.epcs);
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
      canSaveRef.current = true;
    });
  }, []);

  useEffect(() => {
    if (!canSaveRef.current) return;
    try {
      if (Object.keys(renglones).length) {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({
            fecha,
            selectedArea,
            selectedProveedor,
            renglones,
            costos,
            epcs: Array.from(epcLeidosRef.current.entries()),
          })
        );
      } else {
        localStorage.removeItem(DRAFT_KEY);
      }
    } catch (e) {
      console.log("Error al guardar borrador", e);
    }
  }, [fecha, selectedArea, selectedProveedor, renglones, costos]);

  // ─── Escaneo ────────────────────────────────────────────────────────────
  const aviso = (icon, title) =>
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
    });

  // Consulta al backend si el código es un PreModelo que se puede recibir.
  // Devuelve { valido } o { motivo }; null si hubo error de red (no se cachea).
  function buscarCodigo(codigo) {
    const cache = cacheRef.current;
    if (cache.has(codigo)) return cache.get(codigo);
    const promesa = axios
      .post(`${URL_ENTRADAS_PRE_MODELOS}/validar`, { codigos: [codigo] }, { headers: headers() })
      .then(({ data }) =>
        data.validos[0] ? { valido: data.validos[0] } : { motivo: data.omitidos[0]?.motivo || "no_encontrado" }
      )
      .catch((err) => {
        console.log(err);
        return null;
      });
    cache.set(codigo, promesa);
    promesa.then((r) => {
      if (!r) cache.delete(codigo);
    });
    return promesa;
  }

  // Si la pistola pegó varios EPC sin separador (48, 72... hex) se separan.
  function procesarEntrada(rawInput) {
    const raw = (rawInput || "").trim();
    if (!raw) return;
    const hex = raw.replace(/\s+/g, "").toUpperCase();
    if (hex.length > 24 && hex.length % 24 === 0 && /^[0-9A-F]+$/.test(hex)) {
      for (let i = 0; i < hex.length; i += 24) procesarCodigoEscaneado(hex.substring(i, i + 24));
      return;
    }
    procesarCodigoEscaneado(raw);
  }

  async function procesarCodigoEscaneado(raw) {
    // El lector puede entregar el EPC SGTIN-96 (24 hex) o el EAN-13 ya decodificado.
    const epcDecoded = epcToEAN13(raw);
    const esTagRFID = !!epcDecoded;
    const epc = raw.replace(/\s+/g, "").toUpperCase();
    const codigo = epcDecoded || raw.trim();

    if (!esTagRFID && !validateEAN13(codigo)) {
      aviso("error", `No es una etiqueta RFID: ${codigo}`);
      return;
    }

    if (esTagRFID) {
      // Mismo tag físico ya contado (o en proceso): no sumar otra pieza.
      if (epcLeidosRef.current.has(epc)) {
        setStats((st) => ({ ...st, repetidas: st.repetidas + 1 }));
        return;
      }
      // Reservar el EPC ANTES del await para bloquear lecturas duplicadas en vuelo
      epcLeidosRef.current.set(epc, "");
    }

    setPendientes((n) => n + 1);
    let r;
    try {
      r = await buscarCodigo(codigo);
    } finally {
      setPendientes((n) => n - 1);
    }

    if (!r) {
      if (esTagRFID) epcLeidosRef.current.delete(epc);
      aviso("error", `No se pudo validar ${codigo}. Vuelve a escanear.`);
      return;
    }

    if (!r.valido) {
      // Ya es Modelo (ya se le dio entrada) u otra etiqueta ajena: se omite.
      if (esTagRFID) epcLeidosRef.current.set(epc, "omitido");
      setStats((st) => ({ ...st, omitidas: st.omitidas + 1 }));
      return;
    }

    // RFID = 1 pieza: se agrega directo al renglón de su modelo/color/talla.
    const v = r.valido;
    if (esTagRFID) epcLeidosRef.current.set(epc, v.proporcion);
    setRenglones((prev) => {
      const actual = prev[v.proporcion];
      return {
        ...prev,
        [v.proporcion]: actual
          ? { ...actual, cantidad: actual.cantidad + 1 }
          : {
              proporcion: v.proporcion,
              preId: v.preArticulo._id,
              codigo: v.preArticulo.codigo,
              nombre: v.preArticulo.nombre,
              colorNombre: v.color?.name || "",
              tallaNombre: v.talla?.name || "",
              cantidad: 1,
            },
      };
    });
    setCostos((prev) =>
      prev[v.preArticulo._id] !== undefined
        ? prev
        : { ...prev, [v.preArticulo._id]: parseFloat(v.preArticulo.costoMX) || 0 }
    );
    setStats((st) => ({ ...st, leidas: st.leidas + 1 }));
  }

  // ─── Tabla ──────────────────────────────────────────────────────────────
  const grupos = useMemo(() => {
    const porPre = {};
    for (const r of Object.values(renglones)) {
      if (!porPre[r.preId]) {
        porPre[r.preId] = { preId: r.preId, codigo: r.codigo, nombre: r.nombre, piezas: 0, renglones: [] };
      }
      porPre[r.preId].piezas += r.cantidad;
      porPre[r.preId].renglones.push(r);
    }
    return Object.values(porPre)
      .sort((a, b) => a.codigo.localeCompare(b.codigo))
      .map((g) => ({
        ...g,
        renglones: g.renglones.sort(
          (a, b) => a.colorNombre.localeCompare(b.colorNombre) || a.tallaNombre.localeCompare(b.tallaNombre)
        ),
      }));
  }, [renglones]);

  const totalPiezas = grupos.reduce((t, g) => t + g.piezas, 0);
  const totalCosto = grupos.reduce((t, g) => t + g.piezas * (parseFloat(costos[g.preId]) || 0), 0);

  function liberarEPCs(proporciones) {
    const set = new Set(proporciones);
    for (const [epc, prop] of epcLeidosRef.current) {
      if (set.has(prop)) epcLeidosRef.current.delete(epc);
    }
  }

  function quitarRenglon(proporcion) {
    liberarEPCs([proporcion]);
    setRenglones((prev) => {
      const { [proporcion]: _, ...resto } = prev;
      return resto;
    });
  }

  function quitarPreModelo(grupo) {
    const props = grupo.renglones.map((r) => r.proporcion);
    liberarEPCs(props);
    setRenglones((prev) => {
      const nuevo = { ...prev };
      for (const p of props) delete nuevo[p];
      return nuevo;
    });
  }

  function limpiarTodo() {
    Swal.fire({
      title: "¿Limpiar todas las lecturas?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, limpiar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    }).then((r) => {
      if (!r.isConfirmed) return;
      epcLeidosRef.current = new Map();
      cacheRef.current = new Map();
      setRenglones({});
      setCostos({});
      setStats({ leidas: 0, repetidas: 0, omitidas: 0 });
    });
  }

  // ─── Guardar ────────────────────────────────────────────────────────────
  const guardar = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;

    const detener = (title, text, icon = "warning") => {
      Swal.fire({ icon, title, text });
    };
    if (!selectedArea) return detener("Área requerida", "Selecciona un almacén antes de guardar");
    if (!selectedProveedor) return detener("Proveedor requerido", "Selecciona un proveedor antes de guardar");
    if (!grupos.length) return detener("Sin piezas", "No hay PreModelos escaneados para guardar.");
    if (pendientes > 0) {
      return detener("Validando lecturas", "Espera a que terminen de validarse las lecturas.");
    }
    const sinCosto = grupos.filter((g) => !(parseFloat(costos[g.preId]) >= 0));
    if (sinCosto.length) {
      return detener("Costo inválido", `Revisa el costo de: ${sinCosto.map((g) => g.codigo).join(", ")}`);
    }

    const filas = grupos
      .map(
        (g) =>
          `<tr><td style="text-align:left;padding:2px 8px">${g.codigo} ${g.nombre}</td>` +
          `<td style="text-align:right;padding:2px 8px"><b>${fmt(g.piezas)}</b></td>` +
          `<td style="text-align:right;padding:2px 8px">${fmtMoney(parseFloat(costos[g.preId]))}</td></tr>`
      )
      .join("");
    const confirmacion = await Swal.fire({
      title: "Confirmar entrada",
      html:
        `<div style="max-height:300px;overflow-y:auto"><table style="width:100%;font-size:14px">${filas}</table></div>` +
        `<hr/><b>${grupos.length} PreModelos → Modelos &nbsp;/&nbsp; ${fmt(totalPiezas)} piezas &nbsp;/&nbsp; ${fmtMoney(totalCosto)}</b>` +
        `<br/><br/>Se convertirán a Modelo y se les dará entrada. Compara las piezas contra tu lista antes de guardar.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, guardar!",
      cancelButtonText: "Cancelar",
    });
    if (!confirmacion.isConfirmed) return;

    submittingRef.current = true;
    setValidaBoton(false);
    try {
      const { data } = await axios.post(
        URL_ENTRADAS_PRE_MODELOS,
        {
          fecha,
          user: user.id,
          areas: selectedArea,
          proveedores: selectedProveedor,
          items: Object.values(renglones).map((r) => ({
            articulosProporcion: r.proporcion,
            cantidad: r.cantidad,
          })),
          costos: Object.fromEntries(grupos.map((g) => [g.preId, parseFloat(costos[g.preId])])),
        },
        { headers: headers() }
      );

      await axios
        .post(
          URL_LOGS,
          {
            tipo: "Compra",
            detalle: `Entrada PreModelos #${data.compra.idCompras} - ${data.modelos.length} modelos, total: ${data.total_cantidad}`,
            user: user.id,
          },
          { headers: headers() }
        )
        .catch((err) => console.log(err));

      localStorage.removeItem(DRAFT_KEY);
      await Swal.fire({
        icon: "success",
        title: `Entrada #${data.compra.idCompras} guardada`,
        html: `${data.modelos.length} PreModelos convertidos a Modelo<br/>${fmt(data.total_cantidad)} piezas · ${fmtMoney(data.total_costo)}`,
      });
      window.location.reload();
    } catch (error) {
      console.error("Error al guardar entrada de PreModelos:", error);
      submittingRef.current = false;
      setValidaBoton(true);
      const resp = error.response?.data;
      const detalle = Array.isArray(resp?.detalle)
        ? "<br/><br/>" + resp.detalle.map((d) => `${d.codigo || "?"}: ${d.motivo}`).join("<br/>")
        : "";
      Swal.fire({
        icon: "error",
        title: "No se guardó la entrada",
        html: `${resp?.message || "Algo salió mal"}${detalle}`,
      });
    }
  };

  if (!(user?.pedidos_create || user?.entradasTiendas)) {
    return (
      <>
        <Header />
      </>
    );
  }

  return (
    <>
      <Header />
      <br />
      <br />
      <div className="card container col-12 p-4">
        <h3 align="center">Entrada PreModelos</h3>
        <p className="text-center text-muted mb-4">
          Solo se reciben PreModelos que todavía no son Modelo. Al guardar se convierten a Modelo y se les da entrada.
        </p>
        <Row>
          <Col md={2}>
            <Label>Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
          </Col>
          <Col md={2}>
            <Label>Almacen Destino</Label>
            <Input
              type="select"
              value={selectedArea}
              disabled={user.areas != AREAS_GENERAL}
              onChange={(e) => setSelectedArea(e.target.value)}
            >
              <option value="">Selecciona un Almacen</option>
              {areas
                .filter((a) => user.areas == AREAS_GENERAL || user.areas == a._id)
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
            </Input>
          </Col>
          <Col md={2}>
            <Label>Proveedor</Label>
            <Input type="select" value={selectedProveedor} onChange={(e) => setSelectedProveedor(e.target.value)}>
              <option value="">Selecciona un Proveedor</option>
              {[...proveedores]
                .sort((a, b) => (a.nombre_comercial > b.nombre_comercial ? 1 : -1))
                .map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.nombre_comercial}
                  </option>
                ))}
            </Input>
          </Col>
        </Row>
        <br />

        <Row className="align-items-end">
          <Col md={3}>
            <Label>Escanea RFID</Label>
            <Input
              type="text"
              autoComplete="off"
              autoFocus
              innerRef={rfidScanRef}
              placeholder="Pasa la pistola..."
              onChange={(event) => {
                // EPC completo (24 hex, cabecera 30): se procesa sin esperar Enter.
                const inputEl = event.target;
                const hex = inputEl.value.replace(/\s+/g, "").toUpperCase();
                if (/^30[0-9A-F]{22}$/.test(hex)) {
                  inputEl.value = "";
                  procesarEntrada(hex);
                }
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || (event.key === "Tab" && event.target.value.trim())) {
                  event.preventDefault();
                  const inputEl = event.target;
                  const raw = inputEl.value;
                  // Limpiar AHORA (síncrono) para que el siguiente tag caiga en un campo vacío.
                  inputEl.value = "";
                  procesarEntrada(raw);
                }
              }}
            />
          </Col>
          <Col md={7}>
            {(stats.leidas > 0 || stats.repetidas > 0 || stats.omitidas > 0) && (
              <small className="text-muted d-block">
                RFID: {fmt(stats.leidas)} leídas · {fmt(stats.repetidas)} repetidas · {fmt(stats.omitidas)} omitidas
              </small>
            )}
          </Col>
          <Col md={2} className="text-end">
            <Button size="sm" color="danger" outline onClick={limpiarTodo} disabled={!grupos.length && !stats.omitidas}>
              Limpiar lecturas
            </Button>
          </Col>
        </Row>
        <br />

        <Table size="sm" bordered responsive>
          <thead>
            <tr>
              <th>PreModelo</th>
              <th>Color</th>
              <th>Talla</th>
              <th className="text-end">Piezas</th>
              <th style={{ width: 140 }}>Costo pieza</th>
              <th className="text-end">Importe</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {grupos.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  Aún no hay PreModelos escaneados
                </td>
              </tr>
            )}
            {grupos.map((g) => (
              <React.Fragment key={g.preId}>
                <tr className="table-light">
                  <td>
                    <b>{g.codigo}</b> {g.nombre}
                  </td>
                  <td colSpan={2}></td>
                  <td className="text-end">
                    <b>{fmt(g.piezas)}</b>
                  </td>
                  <td>
                    <Input
                      bsSize="sm"
                      type="number"
                      min="0"
                      step="0.01"
                      value={costos[g.preId] ?? ""}
                      onChange={(e) => setCostos((prev) => ({ ...prev, [g.preId]: e.target.value }))}
                    />
                  </td>
                  <td className="text-end">
                    <b>{fmtMoney(g.piezas * (parseFloat(costos[g.preId]) || 0))}</b>
                  </td>
                  <td className="text-center">
                    <Button size="sm" color="danger" outline onClick={() => quitarPreModelo(g)} title="Quitar PreModelo">
                      ✕
                    </Button>
                  </td>
                </tr>
                {g.renglones.map((r) => (
                  <tr key={r.proporcion}>
                    <td></td>
                    <td>{r.colorNombre}</td>
                    <td>{r.tallaNombre}</td>
                    <td className="text-end">{fmt(r.cantidad)}</td>
                    <td></td>
                    <td></td>
                    <td className="text-center">
                      <Button size="sm" color="link" className="text-danger p-0" onClick={() => quitarRenglon(r.proporcion)} title="Quitar renglón">
                        quitar
                      </Button>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          {grupos.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={3} className="text-end">
                  <b>{grupos.length} PreModelos</b>
                </td>
                <td className="text-end">
                  <b>{fmt(totalPiezas)}</b>
                </td>
                <td></td>
                <td className="text-end">
                  <b>{fmtMoney(totalCosto)}</b>
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </Table>

        <Row>
          <Col md={1}>
            <Button className="btn btn-success" onClick={guardar} disabled={!validaBoton || pendientes > 0}>
              Guardar
            </Button>
          </Col>
          <Col md={1}>
            <Button href="/ListadoCompras" className="btn btn-danger" id="botonListado">
              Regresar
            </Button>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default EntradasPreModelos;
